import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useCollectionsSettings,
  usePauseSuspension,
  useSaveCollectionsSettings,
  useTenantBillingStatus,
} from '../../api/billing';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
const day = (iso: string) => new Date(iso).toLocaleDateString();

/**
 * Admin → Pricing plans: automatic suspension for unpaid invoices (off until
 * turned on) and the grace period after the due date.
 */
export function CollectionsSettingsCard() {
  const settings = useCollectionsSettings();
  const save = useSaveCollectionsSettings();
  const toast = useToast();
  const [enabled, setEnabled] = useState(false);
  const [graceDays, setGraceDays] = useState('7');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (settings.data) {
      setEnabled(settings.data.autoSuspendEnabled);
      setGraceDays(String(settings.data.graceDays));
    }
  }, [settings.data]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const days = Number(graceDays);
    if (!Number.isInteger(days) || days < 1 || days > 60) {
      setError('Enter a whole number of days from 1 to 60.');
      return;
    }
    setError(undefined);
    try {
      await save.mutateAsync({ autoSuspendEnabled: enabled, graceDays: days });
      toast.success('Saved.');
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setError(fields.graceDays);
      toast.error(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Card title="Unpaid invoices">
      {settings.isPending ? (
        <PageState kind="loading" />
      ) : settings.isError ? (
        <PageState kind="error" message={settings.error.message} />
      ) : (
        <form
          onSubmit={(e) => void onSubmit(e)}
          noValidate
          style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 520 }}
        >
          <p style={{ margin: 0, ...muted }}>
            Reminders go out 3 days before the due date and 1, 3 and 7 days after. With automatic
            suspension on, owners/admins are warned 2 days before, and a workspace whose
            subscription invoice is still unpaid after the grace period is suspended: they can sign
            in, see their data and pay, but changes, WhatsApp messages, automations and campaigns
            stop until it is paid. Paying lifts it at once. Upgrade invoices never suspend.
          </p>
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Suspend workspaces with unpaid invoices automatically
          </label>
          <TextField
            label="Grace period after the due date (days)"
            type="number"
            min={1}
            max={60}
            value={graceDays}
            onChange={(e) => {
              setGraceDays(e.target.value);
              setError(undefined);
            }}
            error={error}
          />
          <div>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

/**
 * Admin → Tenants → Billing: is this workspace overdue / suspended, and
 * "Give more time" (lifts a suspension and pauses it for N days).
 */
export function TenantSuspensionPanel({ tenantId }: { tenantId: string }) {
  const status = useTenantBillingStatus(tenantId);
  const pause = usePauseSuspension(tenantId);
  const toast = useToast();
  const [days, setDays] = useState('7');
  const [error, setError] = useState<string | undefined>();

  if (status.isPending) return <PageState kind="loading" />;
  if (status.isError) return <PageState kind="error" message={status.error.message} />;
  const s = status.data;

  async function onPause() {
    const n = Number(days);
    if (!Number.isInteger(n) || n < 1 || n > 30) {
      setError('Enter 1 to 30 days.');
      return;
    }
    setError(undefined);
    try {
      await pause.mutateAsync(n);
      toast.success(`No suspension for ${n} days.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not pause suspension.');
    }
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-base)' }}>
        Payment status
      </h3>
      <p style={{ margin: '0 0 var(--space-2)' }}>
        {s.suspended ? (
          <span className="ne-badge" style={{ color: 'var(--color-danger)' }}>
            Suspended since {day(s.suspendedAt!)}
          </span>
        ) : s.overdueInvoice ? (
          <span className="ne-badge">Overdue</span>
        ) : (
          <span className="ne-badge" style={{ color: 'var(--color-success)' }}>
            Up to date
          </span>
        )}{' '}
        {s.overdueInvoice ? (
          <span style={muted}>
            {s.overdueInvoice.invoiceNumber} was due {day(s.overdueInvoice.dueAt)}
            {s.overdueInvoice.suspendsOn && !s.suspended
              ? ` · suspends on ${day(s.overdueInvoice.suspendsOn)}`
              : ''}
          </span>
        ) : null}
        {s.pausedUntil ? (
          <span style={muted}> · suspension paused until {day(s.pausedUntil)}</span>
        ) : null}
      </p>
      {s.suspended || s.overdueInvoice ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
          <TextField
            label="Give more time (days)"
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => {
              setDays(e.target.value);
              setError(undefined);
            }}
            error={error}
          />
          <div style={{ paddingTop: 'var(--space-5)' }}>
            <Button
              type="button"
              variant="secondary"
              disabled={pause.isPending}
              onClick={() => void onPause()}
            >
              {s.suspended ? 'Lift suspension' : 'Pause suspension'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
